const SHOW_DELAY = 140;
const REVEAL_DURATION = 360;
const MIN_IMAGE_EDGE = 72;
const REVEAL_EASING = 'cubic-bezier(0.165, 0.84, 0.44, 1)';
const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
const records = new WeakMap();
const activeRecords = new Set();

const getSourceKey = (image) =>
  [
    image.getAttribute('src'),
    image.getAttribute('srcset'),
    image.getAttribute('sizes'),
    image.getAttribute('loading'),
    image.getAttribute('fetchpriority'),
  ].join('|');

const hasRasterSource = (image) => {
  const source = image.currentSrc || image.src;

  if (!source || source.startsWith('data:')) {
    return false;
  }

  try {
    return !new URL(source, document.baseURI).pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
};

const isVisibleContentImage = (image) => {
  if (
    !image.isConnected ||
    image.closest('template, [hidden], [data-image-loading="off"]') ||
    !hasRasterSource(image)
  ) {
    return false;
  }

  const styles = window.getComputedStyle(image);
  const bounds = image.getBoundingClientRect();

  return (
    styles.display !== 'none' &&
    styles.visibility !== 'hidden' &&
    Number.parseFloat(styles.opacity) > 0.05 &&
    bounds.width >= MIN_IMAGE_EDGE &&
    bounds.height >= MIN_IMAGE_EDGE
  );
};

const restoreInlineStyles = (image, previousStyles) => {
  if (!previousStyles) {
    return;
  }

  image.style.opacity = previousStyles.opacity;
  image.style.filter = previousStyles.filter;
  image.style.clipPath = previousStyles.clipPath;
  image.style.webkitClipPath = previousStyles.webkitClipPath;
};

const prepareImage = (image, record) => {
  if (!isVisibleContentImage(image) || records.get(image) !== record) {
    record.finish();
    return;
  }

  const styles = window.getComputedStyle(image);
  const baseFilter = styles.filter === 'none' ? '' : ` ${styles.filter}`;

  record.previousStyles = {
    opacity: image.style.opacity,
    filter: image.style.filter,
    clipPath: image.style.clipPath,
    webkitClipPath: image.style.webkitClipPath,
  };
  record.baseOpacity = styles.opacity;
  record.baseFilter = baseFilter;
  record.prepared = true;

  image.dataset.imageLoadingState = 'pending';
  image.style.opacity = '0';
  image.style.filter = `blur(6px)${baseFilter}`;
  image.style.clipPath = 'inset(0 100% 0 0)';
  image.style.webkitClipPath = 'inset(0 100% 0 0)';
};

const revealImage = (image, record) => {
  if (!record.prepared || motionPreference.matches || typeof image.animate !== 'function') {
    record.finish();
    return;
  }

  image.dataset.imageLoadingState = 'revealing';
  record.animation = image.animate(
    [
      {
        opacity: 0,
        filter: `blur(6px)${record.baseFilter}`,
        clipPath: 'inset(0 100% 0 0)',
      },
      {
        opacity: record.baseOpacity,
        filter: `blur(0px)${record.baseFilter}`,
        clipPath: 'inset(0 0% 0 0)',
      },
    ],
    {
      duration: REVEAL_DURATION,
      easing: REVEAL_EASING,
      fill: 'forwards',
    },
  );

  record.animation.finished.then(record.finish, record.finish);
};

const enhanceImage = (image) => {
  if (!(image instanceof HTMLImageElement)) {
    return;
  }

  if (image.dataset.imageLoading === 'off' || image.fetchPriority === 'high') {
    records.get(image)?.finish();
    return;
  }

  const sourceKey = getSourceKey(image);
  const previousRecord = records.get(image);

  if (previousRecord?.sourceKey === sourceKey) {
    return;
  }

  previousRecord?.finish();

  if (image.complete || motionPreference.matches || !hasRasterSource(image)) {
    records.set(image, { sourceKey, finish() {} });
    return;
  }

  const record = {
    animation: null,
    baseFilter: '',
    baseOpacity: '1',
    finished: false,
    prepared: false,
    previousStyles: null,
    showDelayElapsed: false,
    sourceKey,
    timer: 0,
    finish() {
      if (record.finished) {
        return;
      }

      record.finished = true;
      window.clearTimeout(record.timer);
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', record.finish);
      restoreInlineStyles(image, record.previousStyles);
      record.animation?.cancel();
      image.removeAttribute('data-image-loading-state');
      activeRecords.delete(record);
    },
  };

  const handleLoad = async () => {
    window.clearTimeout(record.timer);

    if (record.showDelayElapsed && !record.prepared) {
      prepareImage(image, record);
    }

    try {
      await image.decode();
    } catch {}

    if (records.get(image) !== record) {
      record.finish();
      return;
    }

    revealImage(image, record);
  };

  records.set(image, record);
  activeRecords.add(record);
  image.addEventListener('load', handleLoad, { once: true });
  image.addEventListener('error', record.finish, { once: true });
  record.timer = window.setTimeout(() => {
    record.showDelayElapsed = true;

    if (image.complete) {
      handleLoad();
      return;
    }

    if (image.loading === 'lazy') {
      return;
    }

    prepareImage(image, record);
  }, SHOW_DELAY);
};

const enhanceImagesWithin = (root) => {
  if (root instanceof HTMLImageElement) {
    enhanceImage(root);
  }

  root.querySelectorAll?.('img').forEach(enhanceImage);
};

const stopEnhancingImage = (image) => {
  records.get(image)?.finish();
  records.delete(image);
};

const stopEnhancingImagesWithin = (root) => {
  if (root instanceof HTMLImageElement) {
    stopEnhancingImage(root);
  }

  root.querySelectorAll?.('img').forEach(stopEnhancingImage);
};

enhanceImagesWithin(document);

const imageObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes') {
      window.queueMicrotask(() => enhanceImage(mutation.target));
      return;
    }

    mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) {
        enhanceImagesWithin(node);
      }
    });

    mutation.removedNodes.forEach((node) => {
      if (node instanceof Element && !node.isConnected) {
        stopEnhancingImagesWithin(node);
      }
    });
  });
});

imageObserver.observe(document.body, {
  attributeFilter: ['fetchpriority', 'loading', 'sizes', 'src', 'srcset'],
  attributes: true,
  childList: true,
  subtree: true,
});

motionPreference.addEventListener('change', (event) => {
  if (event.matches) {
    activeRecords.forEach((record) => record.finish());
  }
});
